/**
 * Language switcher — a compact, flag-based control.
 *
 * A real <button> trigger shows the active flag + short code; pressing it opens
 * a small menu of flag + endonym (Slovenčina, English, …). Built on native
 * focusable elements so we get keyboard support and :focus-visible for free.
 *
 * Accessibility: flags alone are NOT accessible, so every flag emoji is marked
 * decorative (aria-hidden) and the language NAME is the accessible label. The
 * trigger carries an aria-label from `t.language.select`, exposes aria-haspopup
 * / aria-expanded, and the menu items mark the current locale with
 * aria-current. The trigger meets the 44px minimum touch target.
 */
import { useEffect, useRef, useState } from 'react';
import { useI18n, LOCALES, type Locale } from '../i18n';

export function LanguageSwitcher({ className }: { className?: string }) {
  const { locale, setLocale, t } = useI18n();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // `LOCALES[0]` is the source locale (sk) and is always present; the find
  // result is only ever undefined for an unknown code, so fall back to it.
  const active = LOCALES.find((l) => l.code === locale) ?? LOCALES[0]!;

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function pick(next: Locale) {
    setLocale(next);
    setOpen(false);
  }

  return (
    <div
      className={`lang-switch${className ? ` ${className}` : ''}`}
      ref={rootRef}
    >
      <button
        type="button"
        className="lang-switch__trigger"
        aria-label={t.language.select}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="lang-switch__flag" aria-hidden="true">
          {active.flag}
        </span>
        <span className="lang-switch__code">{active.short}</span>
      </button>

      {open ? (
        <div className="lang-switch__menu" role="menu">
          {LOCALES.map(({ code, label, flag }) => (
            <button
              key={code}
              type="button"
              role="menuitemradio"
              aria-checked={code === locale}
              className="lang-switch__item"
              onClick={() => pick(code)}
            >
              <span className="lang-switch__flag" aria-hidden="true">
                {flag}
              </span>
              <span className="lang-switch__name">{label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
